import React, { useEffect } from "react";
import AttachedDocuments from "../AttachedDocuments";
import { useCompoundResourceApi } from "../../api/ResourceAPI";
import { UseMutationResult } from "react-query";
import { useGetItemDocuments } from "../../api/360FilesAPI";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import { classNames, LONG_TEXT_WRAP_CLASSES } from "../../utils";

export interface LegalCaseItem {
  external_id: string;
  title: string;
  case_number: string;
  court: string;
  start_date: string;
  end_date?: string | null;
}

const legalCasesFields = [
  {
    id: "title",
    label: "Title",
    type: "text",
    placeholder: "Enter title",
    required: true,
  },
  {
    id: "case_number",
    label: "Case #",
    type: "text",
    placeholder: "Enter case number",
    required: true,
  },
  {
    id: "court",
    label: "Court",
    type: "text",
    placeholder: "Enter court name",
    required: true,
  },
  {
    id: "start_date",
    label: "Start Date",
    type: "date",
    placeholder: "",
    required: true,
  },
  {
    id: "end_date",
    label: "End Date",
    type: "date",
    placeholder: "",
    required: false,
  },
  {
    id: "files",
    label: "Upload Files",
    type: "file",
    placeholder: "",
  },
];

const LegalCaseRenderer: React.FC<{ doc: LegalCaseItem }> = ({ doc }) => {
  const { data: uploadedFiles = [], refetch: refetchUploadedFiles } =
    useGetItemDocuments(doc.external_id, true);

  useEffect(() => {
    refetchUploadedFiles();
  }, [refetchUploadedFiles, doc.external_id]);

  return (
    <div className="min-w-0">
      <h3
        className={classNames(
          "text-gray-700 font-medium text-sm",
          LONG_TEXT_WRAP_CLASSES
        )}
      >
        {doc.title}
      </h3>
      {uploadedFiles.map((file: any) => (
        <div
          className="flex flex-row gap-2 p-1 cursor-pointer items-center min-w-0"
          onClick={() => window.open(file.url, "_blank")}
        >
          <span className="w-4 h-4 flex-shrink-0">
            <PaperClipIcon className="w-4 h-4" />
          </span>
          <p
            className={classNames(
              "text-gray-500 text-xs flex-1",
              LONG_TEXT_WRAP_CLASSES
            )}
          >
            {file.filename}
          </p>
        </div>
      ))}
      <p className="text-gray-500 text-xs">
        {doc.start_date}
        {doc.end_date && ` - ${doc.end_date}`}
      </p>
    </div>
  );
};

const LegalCase: React.FC<{
  report_uuid: string;
  workflow_run_uuid: string;
}> = ({ report_uuid, workflow_run_uuid }) => {
  const {
    data: legalCases,
    isLoading,
    error,
    updateNoInvalidate,
    remove,
    createNoInvalidate,
    invalidateQuery,
  } = useCompoundResourceApi<LegalCaseItem>(
    `itemdal/reports/${report_uuid}/legal_cases`,
    `itemdal/legal_cases`
  );

  return (
    <AttachedDocuments
      title="Legal Cases"
      fields={legalCasesFields}
      items={legalCases}
      sortBy="start_date"
      isLoading={isLoading}
      error={error as string}
      createItemMutation={
        createNoInvalidate as UseMutationResult<any, Error, any>
      }
      updateItemMutation={
        updateNoInvalidate as UseMutationResult<any, Error, any>
      }
      removeItemMutation={remove as UseMutationResult<any, Error, any>}
      invalidateQuery={invalidateQuery}
      confirmRemove={true}
      bgColorClass="bg-blue-800"
      DocRenderer={LegalCaseRenderer}
      report_uuid={report_uuid}
      workflow_run_uuid={workflow_run_uuid}
    />
  );
};

export default LegalCase;
